if (!window.FCTFeaturedProductSwap) {
  window.FCTFeaturedProductSwap = (() => {
    const cache = new Map();
    const inflight = new Map();
    const cacheLimit = 3;

    const getShopRoot = () => {
      const root = window.Shopify?.routes?.root;
      return typeof root === 'string' && root.trim()
        ? (root.endsWith('/') ? root : `${root}/`)
        : '/';
    };

    const cacheResponse = (handle, html) => {
      if (cache.has(handle)) cache.delete(handle);
      cache.set(handle, html);
      while (cache.size > cacheLimit) cache.delete(cache.keys().next().value);
      return html;
    };

    const fetchProductPage = (handle) => {
      if (cache.has(handle)) return Promise.resolve(cache.get(handle));
      if (inflight.has(handle)) return inflight.get(handle);

      const url = `${getShopRoot()}products/${encodeURIComponent(handle)}`;
      const request = fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Failed to load product ${handle}: ${response.status}`);
          return response.text();
        })
        .then((html) => cacheResponse(handle, html))
        .finally(() => inflight.delete(handle));

      inflight.set(handle, request);
      return request;
    };

    const cloneScript = (script) => {
      const replacement = document.createElement('script');
      Array.from(script.attributes).forEach(({ name, value }) => replacement.setAttribute(name, value));
      replacement.textContent = script.textContent;
      return replacement;
    };

    const reviveScripts = (root) => {
      Array.from(root.querySelectorAll('script')).forEach((script) => {
        const src = script.getAttribute('src');
        const isBoldMetricsBootstrap = src && src.includes('ssc.production.js');
        if (src && !isBoldMetricsBootstrap && Array.from(document.scripts).some((existing) => existing !== script && existing.src === script.src)) {
          script.remove();
          return;
        }
        script.replaceWith(cloneScript(script));
      });
    };

    const getProductGrid = (documentFragment) => {
      return documentFragment.querySelector('.shopify-section.product-section .product-grid-container')
        || documentFragment.querySelector('.thb-product-detail.product:not(.featured-product) .product-grid-container');
    };

    const prependSwatchData = (sourceDocument, grid) => {
      const scripts = Array.from(sourceDocument.querySelectorAll('script')).filter((script) => {
        return script.id.startsWith('ProductSwatchData-')
          || script.id.startsWith('ProductPackVariantsData-')
          || script.textContent.includes('fctSwatchData');
      });
      scripts.reverse().forEach((script) => grid.prepend(cloneScript(script)));
    };

    const configureFeaturedSwatches = (grid) => {
      grid.querySelectorAll('[data-product-swatch-groups-root]').forEach((container) => {
        container.dataset.swatchNavigation = 'featured-section';
      });
    };

    const applyFeaturedButtonSettings = (featuredProduct, grid) => {
      const useOutlineButton = featuredProduct.dataset.featuredOutlineButton === 'true';
      grid.querySelectorAll('.single-add-to-cart-button').forEach((button) => {
        button.classList.toggle('outline', useOutlineButton);
      });
    };

    const swap = async (swatchContainer, handle) => {
      const featuredProduct = swatchContainer?.closest('.featured-product');
      const currentGrid = featuredProduct?.querySelector('.product-grid-container');
      if (!featuredProduct || !currentGrid || !handle) throw new Error('Featured Product swap target is missing');

      const currentHandle = swatchContainer.dataset.currentHandle;
      if (currentHandle === handle) return;

      featuredProduct.classList.add('is-product-swapping');
      try {
        const html = await fetchProductPage(handle);
        const sourceDocument = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = getProductGrid(sourceDocument);
        if (!nextGrid) throw new Error(`Product grid was not found for ${handle}`);

        prependSwatchData(sourceDocument, nextGrid);
        configureFeaturedSwatches(nextGrid);
        applyFeaturedButtonSettings(featuredProduct, nextGrid);
        currentGrid.replaceWith(nextGrid);
        reviveScripts(nextGrid);

        await new Promise((resolve) => requestAnimationFrame(resolve));
        window.FCTProductSwatchGroups?.mountAll?.();
        window.FCTFeaturedProductMediaLayout?.mountAll?.();
        document.dispatchEvent(new CustomEvent('featured-product:swap:success', {
          detail: { handle, container: featuredProduct }
        }));
      } finally {
        featuredProduct.classList.remove('is-product-swapping');
      }
    };

    return { swap };
  })();
}
